// State Compliance Modal demo scenarios.
//
// These fixtures drive the three §6.C visual-playground variants:
// - CA happy-path (all products licensed)
// - NY blocked (one product unlicensed in target state → PDF disabled)
// - OR coming-soon (state not yet onboarded)
//
// We use Ron-3's lean ApplicationFixture / TankStateFixture / WeatherReadingFixture
// (single source of truth for MixApplyPage) and the shared MixRecipe shape from
// hooks/useMixRecipe. The tank-volume summary (derived fields batches, acresPerTank,
// totalSprayVolumeGal) is computed at render time inside StateComplianceModal/hooks/useData
// via useMixApplyCalcs, not baked into these fixtures.
import type { MixRecipe } from 'hooks/useMixRecipe/types';

import type {
    ApplicationFixture,
    TankStateFixture,
    WeatherReadingFixture
} from 'pages/MixApplyPage/mockData';

type StateComplianceScenario = {
    application: ApplicationFixture;
    label: string;
    mixRecipe: MixRecipe;
    scenarioId: string;
    tankState: TankStateFixture;
    weather: WeatherReadingFixture | null;
};

const CA_APPLICATION: ApplicationFixture = {
    applicationMethod: 'AERIAL_DRONE',
    applicatorLicense: 'CA-QA-MOCK-44821',
    applicatorLicenseState: 'CA',
    applicatorName: 'John Patel',
    applicatorType: 'COMMERCIAL',
    county: 'Fresno',
    crop: 'Almonds',
    customerAddress: '123 Rural Route 7, Fresno, CA 93706',
    customerName: 'Valley Orchards LLC',
    date: '2026-04-10T14:30:00Z',
    faaNNumber: 'N12345MOCK',
    fieldName: 'Fresno South Quadrant',
    id: 'state-compliance-mock-ca-app-01',
    locationLegalDescription: 'T14S R20E Sec 12 NE1/4',
    state: 'CA',
    targetPest: 'Navel Orangeworm',
    totalAcres: 80
};

const CA_MIX_RECIPE: MixRecipe = {
    mixRecipeId: 'state-compliance-mock-ca-mix-01',
    name: 'Almond Orchard Burndown',
    products: [
        {
            category: 'herbicide',
            cropMatchRateRange: { maximum: 44, minimum: 22 },
            dueDiligences: [],
            epaRegNumber: '524-475-MOCK',
            formulation: 'Soluble Liquid',
            isDicamba: false,
            isRestrictedUse: false,
            labelRateRange: { maximum: 44, minimum: 22 },
            manufacturer: 'Bayer',
            modeOfActionCode: '9',
            productId: 'state-compliance-mock-ca-product-01',
            productName: 'Roundup PowerMAX',
            rate: 32,
            rateUnit: 'oz/ac',
            signalWord: 'caution',
            unlicensedStates: []
        },
        {
            category: 'surfactant',
            dueDiligences: [],
            epaRegNumber: '34704-MOCK-01',
            formulation: 'Emulsifiable Concentrate',
            isDicamba: false,
            isRestrictedUse: false,
            labelRateRange: { maximum: 0.5, minimum: 0.125 },
            manufacturer: 'Helena',
            productId: 'state-compliance-mock-ca-product-02',
            productName: 'Induce NIS',
            rate: 0.25,
            rateUnit: 'gal/ac',
            signalWord: 'caution',
            unlicensedStates: []
        },
        {
            category: 'adjuvant',
            dueDiligences: [],
            epaRegNumber: '1381-MOCK-07',
            formulation: 'Suspension Concentrate',
            isDicamba: false,
            isRestrictedUse: false,
            labelRateRange: { maximum: 4, minimum: 1 },
            manufacturer: 'Winfield United',
            productId: 'state-compliance-mock-ca-product-03',
            productName: 'Class Act NG',
            rate: 2,
            rateUnit: 'qt/ac',
            signalWord: 'caution',
            unlicensedStates: ['HI']
        }
    ]
};

const CA_TANK_STATE: TankStateFixture = {
    appTankGal: 200,
    carrierRateGalPerAc: 10,
    carrierType: 'Water',
    mixTankGal: 400
};

const CA_WEATHER: WeatherReadingFixture = {
    gustMph: 8,
    humidityPct: 55,
    recordedAt: '2026-04-10T12:15:00Z',
    temperatureF: 72,
    windDirection: 'N',
    windSpeedMph: 5
};

const CA_HAPPY_PATH_SCENARIO: StateComplianceScenario = {
    application: CA_APPLICATION,
    label: 'CA Happy Path',
    mixRecipe: CA_MIX_RECIPE,
    scenarioId: 'state-compliance-mock-scenario-ca-happy',
    tankState: CA_TANK_STATE,
    weather: CA_WEATHER
};

const NY_APPLICATION: ApplicationFixture = {
    applicationMethod: 'GROUND_BOOM',
    applicatorLicense: 'NY-PA-MOCK-71239',
    applicatorLicenseState: 'NY',
    applicatorName: 'Sarah Chen',
    applicatorType: 'PRIVATE',
    county: 'Orange',
    crop: 'Corn',
    customerAddress: '44 Orchard Lane, Warwick, NY 10990',
    customerName: 'Warwick Valley Farms Inc',
    date: '2026-04-09T10:00:00Z',
    faaNNumber: null,
    fieldName: 'Warwick Valley Farm',
    id: 'state-compliance-mock-ny-app-01',
    locationLegalDescription: 'Warwick Township, Lot 4, Block 12',
    state: 'NY',
    targetPest: 'Common Ragweed',
    totalAcres: 120
};

const NY_MIX_RECIPE: MixRecipe = {
    mixRecipeId: 'state-compliance-mock-ny-mix-01',
    name: 'Corn Pre-Emergent',
    products: [
        {
            dueDiligences: [
                {
                    requirement:
                        'NY DEC Restricted Use Pesticide certification required for this product',
                    state: 'NY'
                },
                {
                    requirement: 'NJ DEP special permit required for atrazine applications',
                    state: 'NJ'
                }
            ],
            epaRegNumber: '100-497-MOCK',
            isDicamba: false,
            isRestrictedUse: true,
            productId: 'state-compliance-mock-ny-product-01',
            productName: 'Atrazine 4L',
            rate: 1.5,
            rateUnit: 'qt/ac',
            unlicensedStates: ['NY', 'NJ']
        },
        {
            dueDiligences: [],
            epaRegNumber: '34704-MOCK-88',
            isDicamba: false,
            isRestrictedUse: false,
            productId: 'state-compliance-mock-ny-product-02',
            productName: 'Crop Oil Concentrate',
            rate: 1,
            rateUnit: 'qt/ac',
            unlicensedStates: []
        }
    ]
};

const NY_TANK_STATE: TankStateFixture = {
    appTankGal: 150,
    carrierRateGalPerAc: 15,
    carrierType: 'Water',
    mixTankGal: 300
};

const NY_WEATHER: WeatherReadingFixture = {
    gustMph: 11,
    humidityPct: 60,
    recordedAt: '2026-04-09T08:15:00Z',
    temperatureF: 68,
    windDirection: 'NW',
    windSpeedMph: 7
};

const NY_BLOCKED_SCENARIO: StateComplianceScenario = {
    application: NY_APPLICATION,
    label: 'NY Blocked',
    mixRecipe: NY_MIX_RECIPE,
    scenarioId: 'state-compliance-mock-scenario-ny-blocked',
    tankState: NY_TANK_STATE,
    weather: NY_WEATHER
};

const OR_APPLICATION: ApplicationFixture = {
    applicationMethod: 'AERIAL_DRONE',
    applicatorLicense: 'OR-CA-MOCK-30914',
    applicatorLicenseState: 'OR',
    applicatorName: 'Miguel Ramirez',
    applicatorType: 'COMMERCIAL',
    county: 'Marion',
    crop: 'Hazelnuts',
    customerAddress: '2100 Filbert Road, Salem, OR 97301',
    customerName: 'Willamette Nursery Co',
    date: '2026-04-11T09:45:00Z',
    faaNNumber: 'N67890MOCK',
    fieldName: 'Willamette Nursery',
    id: 'state-compliance-mock-or-app-01',
    locationLegalDescription: 'T7S R2W Sec 19 SW1/4',
    state: 'OR',
    targetPest: 'Eastern Filbert Blight',
    totalAcres: 40
};

const OR_MIX_RECIPE: MixRecipe = {
    mixRecipeId: 'state-compliance-mock-or-mix-01',
    name: 'Hazelnut Orchard Maintenance',
    products: [
        {
            dueDiligences: [],
            epaRegNumber: '524-MOCK-445',
            isDicamba: false,
            isRestrictedUse: false,
            productId: 'state-compliance-mock-or-product-01',
            productName: 'Glyphosate 41%',
            rate: 22,
            rateUnit: 'oz/ac',
            unlicensedStates: []
        }
    ]
};

const OR_TANK_STATE: TankStateFixture = {
    appTankGal: 100,
    carrierRateGalPerAc: 8,
    carrierType: 'Water',
    mixTankGal: 200
};

const OR_WEATHER: WeatherReadingFixture = {
    gustMph: 6,
    humidityPct: 70,
    recordedAt: '2026-04-11T07:30:00Z',
    temperatureF: 65,
    windDirection: 'SW',
    windSpeedMph: 4
};

const OR_COMING_SOON_SCENARIO: StateComplianceScenario = {
    application: OR_APPLICATION,
    label: 'OR Coming Soon',
    mixRecipe: OR_MIX_RECIPE,
    scenarioId: 'state-compliance-mock-scenario-or-coming-soon',
    tankState: OR_TANK_STATE,
    weather: OR_WEATHER
};

const STATE_COMPLIANCE_SCENARIOS: StateComplianceScenario[] = [
    CA_HAPPY_PATH_SCENARIO,
    NY_BLOCKED_SCENARIO,
    OR_COMING_SOON_SCENARIO
];

const DEFAULT_SCENARIO = CA_HAPPY_PATH_SCENARIO;

export type { StateComplianceScenario };

export {
    CA_HAPPY_PATH_SCENARIO,
    DEFAULT_SCENARIO,
    NY_BLOCKED_SCENARIO,
    OR_COMING_SOON_SCENARIO,
    STATE_COMPLIANCE_SCENARIOS
};
